import { Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { VesselRecord, WorkOrderRecord } from "@smartship-services/core";
import {
  modifyData,
  useData,
  useDataList,
} from "@smartship-services/core/hooks";
import { SaveButton, ResetButton } from "@smartship-services/ui/buttons";
import {
  DateField,
  MultipleSelect,
  QuickParagraphField,
  QuickTextField,
  SelectVessel,
} from "@smartship-services/ui/forms";

const workOrderCategories = ["inventory", "inspection", "maintenance", "other"];

const EditWorkOrderPage = () => {
  const { workOrderId = "" } = useParams();
  const workOrder = useData<WorkOrderRecord>("workOrders", workOrderId);
  const vessels = useDataList<VesselRecord>("vessels");
  const [vesselId, setVesselId] = useState<string>("");
  const [formValues, setFormValues] = useState<Partial<WorkOrderRecord>>({});
  const navigate = useNavigate();

  const handleFormValues = (
    field: string,
    newValue: Date | string | string[] | null,
  ) => {
    if (!workOrder) return;
    setFormValues((prev) => ({ ...prev, [field]: newValue }));
  };

  const saveForm = () => {
    if (!workOrder) return;

    const updatedWorkOrder = {
      ...workOrder,
      ...formValues,
      vesselId,
    };

    try {
      modifyData("workOrders", workOrderId, updatedWorkOrder);
      navigate(`/work-orders/${workOrderId}`);
    } catch (err) {
      alert("Error saving work order");
      console.error(err);
    }
  };

  useEffect(() => {
    if (workOrder) {
      setVesselId(workOrder.vesselId || "");
      // Set initial vessel in session storage for SelectVessel component
      const vessel = vessels?.find((v) => v.id === workOrder.vesselId);
      if (vessel) {
        sessionStorage.setItem(
          "activeVessel",
          JSON.stringify({ id: vessel.id, label: vessel.vesselName }),
        );
      }
    }
  }, [workOrder, vessels]);

  if (!workOrder) {
    return (
      <main>
        <Typography>No work order found with this ID.</Typography>
      </main>
    );
  }

  return (
    <main>
      <Typography variant="h1">Edit Work Order</Typography>
      <Stack spacing={1.5} maxWidth={600} sx={{ mb: 2, mt: 2 }}>
        <SelectVessel
          setter={(newId) => setVesselId(newId || "")}
          disabled={true}
        />
        <MultipleSelect
          label="Categories"
          options={workOrderCategories}
          setter={(newValue) => handleFormValues("categories", newValue)}
          value={formValues.categories || workOrder.categories || []}
        />
        <QuickTextField
          label="Customer Name"
          setter={(newValue) => handleFormValues("customerName", newValue)}
          value={formValues.customerName ?? workOrder.customerName}
        />
        <DateField
          label="Planned Start Date"
          setter={(newValue) => handleFormValues("plannedStartDate", newValue)}
          value={formValues.plannedStartDate ?? workOrder.plannedStartDate}
        />
        <QuickTextField
          label="Onboard Contact"
          setter={(newValue) =>
            handleFormValues("onboardContactName", newValue)
          }
          value={formValues.onboardContactName ?? workOrder.onboardContactName}
        />
        <QuickTextField
          label="Service Engineers"
          setter={(newValue) =>
            handleFormValues("serviceEngineers", newValue.split(", "))
          }
          value={(
            formValues.serviceEngineers ?? workOrder.serviceEngineers
          ).join(", ")}
        />
        <QuickParagraphField
          label="Notes"
          setter={(newValue) => handleFormValues("freeComment", newValue)}
          value={formValues.freeComment ?? workOrder.freeComment}
        />
        <Stack direction="row" justifyContent="space-between" spacing={1}>
          <SaveButton handler={() => saveForm()} />
        </Stack>
      </Stack>
    </main>
  );
};

export default EditWorkOrderPage;
